<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import * as callAPIFind from '/@src/composable/callableApi'
import { useField } from 'vee-validate'
import { CiselnikType } from '/@src/pages/app/vyzvy/[detailId]/zoppm/validationSchema'
import { skSelectOption } from '/@src/constants'

type AdresaFieldType = Required<CiselnikType> | undefined

export interface MiestoRealizacieProps {
  miestoNumber: number
  name: string
}

const props = defineProps<MiestoRealizacieProps>()
const { t } = useI18n()
const OPTIONS_STAT = ref<ReadonlyArray<CiselnikType>>()
const OPTIONS_KRAJ = ref<ReadonlyArray<CiselnikType>>()
const OPTIONS_OKRES = ref<ReadonlyArray<CiselnikType>>()
const OPTIONS_OBEC = ref<ReadonlyArray<CiselnikType>>()

onMounted(() => {
  // TODO: zatial je stat len fixne SK, do buducna sa moze menit
  OPTIONS_STAT.value = [skSelectOption]
})

const { value: lokalita } = useField(`${props.name}`)
const { value: statField } = useField<AdresaFieldType>(
  `${props.name}.adresa.stat`,
  undefined
)

const { value: krajField, setValue: setKrajValue } = useField<AdresaFieldType>(
  `${props.name}.adresa.kraj`,
  undefined
)

const { value: okresField, setValue: setOkresValue } = useField<AdresaFieldType>(
  `${props.name}.adresa.okres`,
  undefined
)

const { setValue: setObecValue } = useField<AdresaFieldType>(
  `${props.name}.adresa.obec`,
  undefined
)

const fetchOptionsKraj = async (id: string) => {
  OPTIONS_KRAJ.value = await callAPIFind.getKrajRaw(id)
}

const fetchOptionsOkres = async (id: string) => {
  OPTIONS_OKRES.value = await callAPIFind.getOkresRaw(id)
}

const fetchOptionsObec = async (id: string) => {
  OPTIONS_OBEC.value = await callAPIFind.getObecRaw(id)
}

watch(
  [statField, krajField, okresField],
  async ([nextStat, nextKraj, nextOkres], [prevStat, prevKraj, prevOkres]) => {
    // Kontrolujeme, či došlo k zmene štátu
    if (nextStat && nextStat.id !== prevStat?.id) {
      // Ak bol zmenený predtým valídne zvolený štát, musíme resetovať všetky náväzné hodnoty (kraj, obec, okres)
      // vo validačnom kontexte.
      // Pri prvotnom načítaní (e.g reload stránky) tieto hodnoty neresetujeme, nakoľko vo validačnom kontexte
      // môžu byť valídne hodnoty z BE ktoré treba už len vyzobraziť - tie by sa neprávom zresetovali.
      if (prevStat !== undefined) {
        setKrajValue(undefined)
        setOkresValue(undefined)
        setObecValue(undefined)
        OPTIONS_KRAJ.value = undefined
        OPTIONS_OKRES.value = undefined
        OPTIONS_OBEC.value = undefined
      }
      await fetchOptionsKraj(String(nextStat.id))
    }

    // Načítame číselník v prípade že je zadaná hodnota
    // ale možnosti sú prázdne (deje sa pri duplikovaní miesta realizácie)
    if (nextStat?.id && !OPTIONS_KRAJ.value?.length) {
      await fetchOptionsKraj(String(nextStat.id))
    }

    // Kontrolujeme, či došlo k zmene kraja
    if (nextKraj && nextKraj.id !== prevKraj?.id) {
      // Robíme reset názväzných hodnôt kraja, viď komentár vyššie k štátu.
      if (prevKraj !== undefined) {
        setOkresValue(undefined)
        setObecValue(undefined)
        OPTIONS_OKRES.value = undefined
        OPTIONS_OBEC.value = undefined
      }
      await fetchOptionsOkres(String(nextKraj.id))
    }

    // Načítame číselník v prípade že je zadaná hodnota
    // ale možnosti sú prázdne (deje sa pri duplikovaní miesta realizácie)
    if (nextKraj?.id && !OPTIONS_OKRES.value?.length) {
      await fetchOptionsOkres(String(nextKraj.id))
    }

    // Kontrolujeme, či došlo k zmene okresu
    if (nextOkres && nextOkres.id !== prevOkres?.id) {
      // Robíme reset názväzných hodnôt okresu, viď komentár vyššie k štátu.
      if (prevOkres !== undefined) {
        setObecValue(undefined)
        OPTIONS_OBEC.value = undefined
      }
      await fetchOptionsObec(String(nextOkres.id))
    }

    // Načítame číselník v prípade že je zadaná hodnota
    // ale možnosti sú prázdne (deje sa pri duplikovaní miesta realizácie)
    if (nextOkres?.id && !OPTIONS_OBEC.value?.length) {
      await fetchOptionsObec(String(nextOkres.id))
    }

    if (lokalita.value?.poradie >= 0) {
      if (!nextStat && prevStat) {
        setKrajValue(undefined)
        setOkresValue(undefined)
        setObecValue(undefined)
        OPTIONS_KRAJ.value = undefined
        OPTIONS_OKRES.value = undefined
        OPTIONS_OBEC.value = undefined
      }

      if (!nextKraj && prevKraj) {
        setOkresValue(undefined)
        setObecValue(undefined)
        OPTIONS_OKRES.value = undefined
        OPTIONS_OBEC.value = undefined
      }

      if (!nextOkres && prevOkres) {
        setObecValue(undefined)
        OPTIONS_OBEC.value = undefined
      }
    }
  },
  {
    // Nastavujeme flush:sync - callback watchu niekedy zbehne dvakrát, medzi tým ako
    // čakáme na výsledok volania API z callbacku A nám zbieha callback B, ktorý však
    // pracuje so starými hodnotami kraju, okresu, obce a tie sa zdá sa opäť nastavia
    // do validačného kontextu. Tým pádom reset týchto hodnôt v callbacku A nemá žiaden efekt.
    flush: 'sync',
  }
)
</script>

<template>
  <template v-if="props.name">
    <ValidationWrapper
      v-if="OPTIONS_STAT"
      v-slot="{ modelValue, ...slotProps }"
      :name="`${props.name}.adresa.stat`"
      :value="OPTIONS_STAT?.[0]"
    >
      <IDSKAutocomplete
        v-bind="slotProps"
        :id="`${props.name}.adresa.stat`"
        :key="modelValue"
        :label="t('zoppm.step6.stat')"
        :options="OPTIONS_STAT"
        full-width
        option-object-label="nazov"
        option-object-value="id"
        :model-value="modelValue"
      />
    </ValidationWrapper>

    <ValidationWrapper
      v-slot="{ modelValue, ...slotProps }"
      :name="`${props.name}.adresa.kraj`"
    >
      <IDSKAutocomplete
        v-bind="slotProps"
        :id="`${props.name}.adresa.kraj`"
        :key="modelValue"
        show-empty-option
        update-on-blur
        :label="t('zoppm.step6.kraj')"
        :options="OPTIONS_KRAJ"
        full-width
        option-object-label="nazov"
        option-object-value="id"
        :model-value="modelValue"
      />
    </ValidationWrapper>

    <ValidationWrapper
      v-slot="{ modelValue, ...slotProps }"
      :name="`${props.name}.adresa.okres`"
    >
      <IDSKAutocomplete
        v-bind="slotProps"
        :id="`${props.name}.adresa.okres`"
        :key="modelValue"
        show-empty-option
        update-on-blur
        :label="t('zoppm.step6.okres')"
        :options="OPTIONS_OKRES"
        full-width
        option-object-label="nazov"
        option-object-value="id"
        :model-value="modelValue"
      />
    </ValidationWrapper>

    <ValidationWrapper
      v-slot="{ modelValue, ...slotProps }"
      :name="`${props.name}.adresa.obec`"
    >
      <IDSKAutocomplete
        v-bind="slotProps"
        :id="`${props.name}.adresa.obec`"
        :key="modelValue"
        show-empty-option
        update-on-blur
        :label="t('zoppm.step6.obec')"
        :options="OPTIONS_OBEC"
        option-object-label="nazov"
        option-object-value="id"
        full-width
        :model-value="modelValue"
      />
    </ValidationWrapper>

    <ValidationWrapper v-slot="slotProps" :name="`${props.name}.adresa.psc`">
      <IDSKInput
        v-bind="slotProps"
        :id="`${props.name}.adresa.psc`"
        :label="t('zoppm.step6.psc')"
        type="number"
      />
    </ValidationWrapper>

    <ValidationWrapper v-slot="slotProps" :name="`${props.name}.adresa.ulica`">
      <IDSKInput
        v-bind="slotProps"
        :id="`${props.name}.adresa.ulica`"
        :label="t('zoppm.step6.ulica')"
      />
    </ValidationWrapper>

    <ValidationWrapper v-slot="slotProps" :name="`${props.name}.adresa.supisneCislo`">
      <IDSKInput
        v-bind="slotProps"
        :id="`${props.name}.adresa.supisneCislo`"
        :label="t('globalne.supisneCislo')"
        :hint="t('globalne.supisneCisloHint')"
      />
    </ValidationWrapper>

    <ValidationWrapper v-slot="slotProps" :name="`${props.name}.adresa.orientacneCislo`">
      <IDSKInput
        v-bind="slotProps"
        :id="`${props.name}.adresa.orientacneCislo`"
        :label="t('globalne.orientacneCislo')"
        :hint="t('globalne.orientacneCisloHint')"
      />
    </ValidationWrapper>
  </template>
</template>

<style lang="scss"></style>
